<template>
	<div class="project-route">
	<div class="project-module" v-if="isReady && selectedProject">
		<header class="project-workspace-header">
			<div class="project-breadcrumb">
				<router-link to="/projects">
					<portal-icon name="arrow-left" :size="17"></portal-icon>
					{{ $t('projects.page-heading') }}
				</router-link>
			</div>
			<div class="project-heading-row">
				<div>
					<h1>{{ selectedProject.name }}</h1>
					<div class="project-heading-meta">
						<portal-status-pill
							:background="selectedProject.active ? 'var(--v-success_10-base)' : 'var(--v-gray_20-base)'"
							:color="selectedProject.active ? 'var(--v-success-base)' : 'var(--portal-muted)'"
						>
							{{ $t(projectStatusKey(selectedProject)) }}
						</portal-status-pill>
						<span>
							<portal-icon name="calendar-days" :size="15"></portal-icon>
							{{ $t('global.due') }} {{ formatProjectDueDate(selectedProject) || $t('projects.no-due-date') }}
						</span>
					</div>
				</div>
			</div>
			<p v-if="isProjectSuspended(selectedProject)" class="font-14" role="status">{{ $t('projects.lifecycle-read-only') }}</p>
			<nav class="project-view-nav" :aria-label="$t('projects.project')">
				<button
					v-for="page in availableViews"
					:key="page.value"
					type="button"
					:class="{ active: view === page.value }"
					:aria-current="view === page.value ? 'page' : null"
					@click="setView(page.value)"
				>
					<portal-icon :name="page.icon" :size="17"></portal-icon>
					{{ page.label }}
				</button>
			</nav>
		</header>

		<div class="project-toolbar" v-if="planningViews.includes(view)">
			<portal-filter-bar :label="$t('global.filter')">
				<portal-search-field
					v-model="taskFilter"
					:label="$t('global.filter')"
					:placeholder="$t('global.filter')"
					:clear-label="$t('global.clear')"
				></portal-search-field>
				<portal-select-field
					v-model="taskStatus"
					:label="$t('projects.filter.status')"
					:options="taskStatusOptions"
				></portal-select-field>
			</portal-filter-bar>
		</div>

		<div class="project-container show-scrollbar">
			<section v-if="view === 'overview'" class="project-detail-shell">
				<project
					:key="selectedProject.id"
					:project="selectedProject"
					:status-list="getStatusList(selectedProject.projectTypeId)"
					compact-header
				></project>
			</section>
			<div v-if="view === 'tasks' && canManageProject">
				<project-kanban
					:read-only="isProjectSuspended(selectedProject)"
					:assignable-users="assignableUsers"
					:has-full-access-projects="canEditProject(selectedProject) && !selectedProject.portalAccessAssignedOnly"
					:deliverables="filteredDeliverables"
					:is-collaborator="true"
					:columns="activeProjectType.statusList"
					:header-height="headerHeight"
					@edit-deliverable="editDeliverable($event)"
					@update-kanban-props="updateKanbanProperties($event)"
					@add-deliverable="addDeliverable($event)"
					@add-subtask="createSubtask($event)"
				></project-kanban>
			</div>
			<project-task-table
				v-if="view === 'list' && canManageProject"
				:deliverables="filteredDeliverables"
				:status-list="activeProjectType.statusList"
				:assignable-users="assignableUsers"
				@open-deliverable="editDeliverable($event)"
			></project-task-table>

			<div v-if="view === 'calendar' && canManageProject" class="planning-surface show-scrollbar">
				<project-calendar
					:projects="[selectedProject]"
					:deliverables="filteredDeliverables"
					:status-list="activeProjectType.statusList"
					@edit-deliverable="editDeliverable($event)"
					@update-due-date="datesUpdated($event)"
				>
				</project-calendar>
			</div>
			<project-time
				v-if="view === 'time' && timeEnabled"
				:project-id="selectedProject.id"
			></project-time>
		</div>
	</div>
	<div class="project-loading" v-else-if="!isReady">
		<v-progress-circular indeterminate :size="38" :width="3" color="primary"></v-progress-circular>
	</div>
	<div class="project-loading" v-else>
		<div>{{ $t('timetrack.no-projects') }}</div>
		<router-link to="/projects">{{ $t('projects.page-heading') }}</router-link>
	</div>
	</div>
</template>

<script>
	import { isProjectSuspended, canEditProject, projectStatusKey } from '@/modules/projects/projectLifecycle';
	import Project from '@/modules/projects/Project';
	import ProjectService from '@/modules/projects/ProjectService';
	import ProjectKanban from '@/modules/projects/management/ProjectKanban';
	import DeliverableDetail from '@/modules/projects/deliverable/DeliverableDetail';
	import ProjectCalendar from '@/modules/projects/management/ProjectCalendar';
	import ProjectTaskTable from '@/modules/projects/ProjectTaskTable';
	import ProjectTime from '@/modules/projects/ProjectTime';
	import PortalFilterBar from '@/components/filters/PortalFilterBar';
	import PortalSearchField from '@/components/filters/PortalSearchField';
	import PortalSelectField from '@/components/filters/PortalSelectField';
	import { DateTime } from 'luxon';

	export default {
		name: 'Projects',

		props: {
			v: { type: String, required: false, default: 'overview' },
			projectId: { type: [String, Number], required: true },
		},

		components: {
			ProjectCalendar,
			ProjectKanban,
			Project,
			ProjectTaskTable,
			ProjectTime,
			PortalFilterBar,
			PortalSearchField,
			PortalSelectField,
		},

		data: function() {
			return {
				projectService: new ProjectService(),
				view: 'overview',
				projects: [],
				deliverables: [],
				projectTypes: [],
				projectTypeId: null,
				assignableUsers: [],
				taskFilter: null,
				taskStatus: 'Active',
				headerHeight: 214,
				isReady: false,
				planningViews: ['tasks', 'list', 'calendar'],
			};
		},

		mounted() {
			this.view = this.v || 'overview';

			this.getProjectList();
			this.getAssignableUsers();
			this.$store.state.eventBus.$on(this.getChannelName(), this.eventHandler);
		},

		beforeDestroy() {
			this.$store.state.eventBus.$off(this.getChannelName(), this.eventHandler);
		},

		methods: {
			isProjectSuspended, canEditProject, projectStatusKey,
			setView: function(view) {
				this.view = view;
				this.$router.replace({ path: this.$route.path, query: view === 'overview' ? {} : { v: view } }).catch(() => {});
			},

			getAssignableUsers: function() {
				this.projectService.getAllAssignableUsers().then((res) => {
					this.assignableUsers.splice(0, this.assignableUsers.length);
					this.assignableUsers.push(...res.data);
				});
			},

			getProjectList: function() {
				this.projectService.getProjects().then((res) => {
					this.projects.splice(0, this.projects.length);
					this.projects.push(...res.data);
					this.projects = this.projects.sort(this.sortByActive);
					this.projects = this.projects.sort(this.sortByName);

					this.getProjectTypes();
				}).catch(() => {
					this.isReady = true;
				});
			},

			getProjectTypes: function() {
				this.projectService.getProjectTypes().then((res) => {
					this.projectTypes.push(...res.data);

					if (this.projectTypes.length === 0) {
						this.isReady = true;
						return;
					}

					const selectedTypeId = this.selectedProject && this.selectedProject.projectTypeId;
					if (selectedTypeId && this.projectTypes.some((type) => type.id === selectedTypeId)) {
						this.projectTypeId = selectedTypeId;
					} else if (!this.projectTypeId) {
						let defaultProjectType = this.projectTypes.find((p) => p.isDefaultProjectType);

            if(defaultProjectType) {
              this.projectTypeId = defaultProjectType.id;
            }else{
              this.projectTypeId = this.projectTypes[0].id;
            }
          }

				}).finally(() => {
					this.normalizeView();
					this.isReady = true;
				});
			},

			formatProjectDueDate(project) {
				return project.dueDate
					? DateTime.fromISO(project.dueDate).toLocaleString(DateTime.DATE_MED)
					: '';
			},

			getStatusList(projectTypeId) {
				const projectType = this.getProjectType(projectTypeId);
				return projectType && projectType.statusList ? projectType.statusList : [];
			},


			normalizeView() {
				if (!this.availableViews.some((page) => page.value === this.view)) {
					this.setView('overview');
				}
			},

			getDeliverables: function() {
				this.projectService.getDeliverables(this.projectTypeId).then((res) => {
					this.deliverables.splice(0, this.deliverables.length);
					this.deliverables.push(...res.data);
					this.deliverables.forEach((d) => {
						d.project = this.projects.find((p) => p.id === d.projectId);
					});
				});
			},

			addDeliverable: function(column) {
				if (this.selectedProject && canEditProject(this.selectedProject)) {
					this.createDeliverable(this.selectedProject, column);
				}
			},

			createSubtask: function(parentTask) {
				if (!canEditProject(this.selectedProject)) return;
				let deliverable = {
					id: null,
					name: null,
					parentTaskId: parentTask.id,
					description: null,
					assignedTo: null,
					dueDate: null,
					statusId: this.activeProjectType.statusList[0].id,
					tasks: [],
					comments: [],
					events: [],
					files: [],
					archived: false,
				};
				this.$store.commit('startLoading');
				this.projectService
					.createDeliverable(parentTask.project.id, deliverable)
					.then((res) => {
						this.editDeliverable(res.data);
					})
					.finally(() => this.$store.commit('stopLoading'));
			},

			createDeliverable: function(project, column) {
				let deliverable = {
					id: null,
					name: null,
					description: null,
					assignedTo: null,
					dueDate: null,
					statusId: column.id,
					tasks: [],
					comments: [],
					events: [],
					files: [],
					archived: false,
				};
				this.$store.commit('startLoading');
				this.projectService
					.createDeliverable(project.id, deliverable)
					.then((res) => {
						this.editDeliverable(res.data);
					})
					.finally(() => this.$store.commit('stopLoading'));
			},

			updateKanbanProperties: function(updates) {
				this.projectService.updateKanbanProperties(updates);
			},

			getChannelName() {
				return this.$store.getters.getMessageChannelPrefix + '.pr';
			},

			eventHandler: function(event) {
				if (event.userMetadata === 'ProjectDeliverableMini') {
					this.processDeliverableDataEvent(event.message);
				} else if (event.userMetadata === 'KanbanUpdate') {
					this.processKanbanUpdate(event.message);
				}
			},

			processKanbanUpdate: function(updates) {
				for (let i = 0; i < updates.length; i++) {
					let deliverableId = updates[i].deliverableId;
					let kanbanSort = updates[i].kanbanSort;
					let statusId = updates[i].statusId;

					let ix = this.deliverables.findIndex((d) => d.id === deliverableId);

					if (ix > -1) {
						let deliverable = this.deliverables[ix];
						deliverable.kanbanSort = kanbanSort;
						deliverable.statusId = statusId;
						this.deliverables.splice(ix, 1, deliverable);
					}
				}
			},

			processDeliverableDataEvent: function(deliverable) {
				let project = this.projects.find((p) => p.id === deliverable.projectId);

				if (!project) {
					return;
				}

				if (project.portalAccessAssignedOnly) {
					this.getDeliverables();
					return;
				}

				deliverable.project = project;

				let ix = this.deliverables.findIndex((d) => d.id === deliverable.id);
				if (ix > -1) {
					if (deliverable.statusId === '__deleted__') {
						this.deliverables.splice(ix, 1);
					} else {
						this.deliverables.splice(ix, 1, deliverable);
					}
				} else if (deliverable.statusId !== '__deleted__') {
					this.deliverables.push(deliverable);
				}
			},

			editDeliverable: function(deliverable) {
				let project = this.projects.find((p) => p.id === deliverable.projectId);
				this.projectService.getDeliverableDetail(deliverable.id).then((res) => {
					let binding = {
						origDeliverable: res.data,
						project: project,
						statusList: this.activeProjectType.statusList,
						subtasks: deliverable.subtasks || [],
					};
					this.$store.state.globalModalController.openModal(DeliverableDetail, binding, true, false, 'right-panel').then((res) => {
						if (res) {
							res.project = project;
							let ix = this.deliverables.findIndex((d) => d.id === deliverable.id);
							if (ix > -1) {
								this.deliverables.splice(ix, 1, res);
							} else {
								this.deliverables.push(res);
							}
						}
					});
				});
			},

			datesUpdated: function(dates) {
				let patch = [];
				let ix = this.deliverables.findIndex((d) => d.id === dates.id);
				let deliverable = this.deliverables[ix];

				if (canEditProject(deliverable.project)) {
					deliverable.startDate = dates.startDate;
					deliverable.dueDate = dates.dueDate;
					this.deliverables.splice(ix, 1, deliverable);

					patch.push({
						op: 'replace',
						path: '/dueDate',
						value: dates.dueDate,
					});

					patch.push({
						op: 'replace',
						path: '/startDate',
						value: dates.startDate,
					});

					this.projectService.patchDeliverable(deliverable.id, patch);
				} else {
					this.deliverables.splice(ix, 1, deliverable);
				}
			},

			getDefaultProjectType: function() {
				return this.projectTypes.find((p) => p.isDefaultProjectType);
			},

			getProjectType: function(projectTypeId) {
				let ix = this.projectTypes.findIndex((p) => p.id === projectTypeId);
				if (ix > -1) {
					return this.projectTypes[ix];
				} else {
					return this.getDefaultProjectType();
				}
			},

			sortByActive: function(a, b) {
				if (a.active && b.active) {
					return 0;
				} else if (a.active) {
					return -1;
				} else if (b.active) {
					return 1;
				} else {
					return 0;
				}
			},

			sortByName: function(a, b) {
				return a.name.localeCompare(b.name);
			},

			isInTaskStatusFilter(d) {
				if (this.taskStatus === 'Archived') {
					return d.archived;
				} else if (this.taskStatus === 'All') {
					return true;
				} else {
					return !d.archived;
				}
			},

			isInNameFilter(d) {
				if (this.taskFilter) {
					return d.name && d.name.toLowerCase().includes(this.taskFilter.toLowerCase());
				} else {
					return true;
				}
			},
		},

		computed: {
			activeProjectType: function() {
				return this.projectTypes.find((p) => p.id === this.projectTypeId) || { statusList: [] };
			},

			availableViews() {
				const pages = [
					{
						label: this.$t('projects.overview.heading'),
						value: 'overview',
						icon: 'folder-open',
					},
				];
				if (this.canManageProject) {
					pages.push(
						{ label: this.$t('projects.kanban'), value: 'tasks', icon: 'folder-kanban' },
						{ label: this.$t('projects.list'), value: 'list', icon: 'clipboard-list' },
						{ label: this.$t('projects.calendar'), value: 'calendar', icon: 'calendar-days' }
					);
				}
				if (this.timeEnabled) {
					pages.push({
						label: this.$t('timetrack.page-heading'),
						value: 'time',
						icon: 'timer',
					});
				}
				return pages;
			},

			taskStatusOptions() {
				return [
					{ text: this.$t('projects.filter.active'), value: 'Active' },
					{ text: this.$t('projects.filter.archived'), value: 'Archived' },
					{ text: this.$t('projects.filter.all'), value: 'All' },
				];
			},

			canManageProject() {
				return (
					this.selectedProject &&
					(this.selectedProject.portalAccess === 'Full access' ||
						this.selectedProject.portalAccess === 'Read only')
				);
			},

			timeEnabled() {
				return Boolean(
					this.selectedProject &&
						this.$store.getters.getFeatures.timeEnabled &&
						this.selectedProject.showTimeWorkedInPortal
				);
			},

			filteredDeliverables: function() {
				let protectedIds = new Set();
				let result = [
					...this.deliverables.filter(
						(d) =>
							d.projectTypeId === this.projectTypeId &&
							this.selectedProject &&
							String(d.projectId) === String(this.selectedProject.id)
					),
				];
				let subTaskMap = new Map();

				//get all the subtasks to the bottom, so they get processed first as we loop the list backwards
				result.sort((a, b) => {
					if (a.parentTaskId === null && b.parentTaskId !== null) {
						return -1;
					} else if (a.parentTaskId !== null && b.parentTaskId === null) {
						return 1;
					} else {
						return 0;
					}
				});

				for (let i = result.length - 1; i >= 0; i--) {
					let deliverable = result[i];

					if (protectedIds.has(deliverable.id)) {
						//skip filtering as we are needed based on matching subtask
					} else if (!this.isInTaskStatusFilter(deliverable)) {
						result.splice(i, 1);
						continue;
					} else if (!this.isInNameFilter(deliverable)) {
						result.splice(i, 1);
						continue;
					}
					if (deliverable.parentTaskId) {
						protectedIds.add(deliverable.parentTaskId);
						result.splice(i, 1);

						if (!subTaskMap.has(deliverable.parentTaskId)) {
							subTaskMap.set(deliverable.parentTaskId, []);
						}

						subTaskMap.get(deliverable.parentTaskId).push(deliverable);
					} else {
						if (deliverable.subtasks) {
							deliverable.subtasks.splice(0, deliverable.subtasks.length);
						} else {
							this.$set(deliverable, 'subtasks', []);
						}
					}
				}

				let deliverableMap = new Map(result.map((obj) => [obj.id, obj]));

				subTaskMap.forEach((value, key) => {
					if (deliverableMap.has(key)) {
						//push into subtask array
						deliverableMap.get(key).subtasks.push(...value);
					} else {
						//failsafe to surface subtasks if they get orphaned
						deliverableMap.set(value.id, value);
					}
				});

				result = [...deliverableMap.values()];
				return result;
			},

			selectedProject() {
				return (
					this.projects.find((project) => String(project.id) === String(this.projectId)) || null
				);
			},

			fullAccessProjects: function() {
				return this.selectedProject && canEditProject(this.selectedProject)
					? [this.selectedProject]
					: [];
			},

			fullReadProjects: function() {
				return this.canManageProject ? [this.selectedProject] : [];
			},

			hasAccessToAssignedTasksOnly: function() {
				return Boolean(this.selectedProject && this.selectedProject.portalAccessAssignedOnly);
			},

			hasProjectManagement: function() {
				return this.canManageProject;
			},
		},

		watch: {
			projectTypeId: function() {
				this.deliverables.splice(0, this.deliverables.length);
				if (this.projectTypeId) {
					this.getDeliverables();
				}
			},

			v: function(val) {
				this.view = val || 'overview';
				this.normalizeView();
			},
		},
	};
</script>

<style scoped lang="scss">
	.project-route,
	.project-module {
		min-height: 100%;
	}

	.project-module {
		display: flex;
		flex-direction: column;
	}

	.project-loading {
		min-height: 320px;
		display: grid;
		place-content: center;
		justify-items: center;
		gap: 12px;
		color: var(--portal-muted);
	}

	.project-workspace-header {
		padding: 17px 18px 0;
		border-bottom: 1px solid var(--portal-border);
		background: var(--portal-surface);
	}

	.project-breadcrumb a {
		width: fit-content;
		display: flex;
		align-items: center;
		gap: 6px;
		color: var(--portal-muted);
		font-size: 13px;
		font-weight: 650;
		text-decoration: none;
	}

	.project-breadcrumb a:hover {
		color: var(--v-primary-base);
	}

	.project-heading-row {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 20px;
		padding: 12px 0 17px;
	}

	.project-heading-row h1 {
		margin: 0;
		color: var(--portal-text);
		font-size: clamp(23px, 3vw, 32px);
		font-weight: 700;
		letter-spacing: -0.035em;
	}

	.project-heading-meta {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-top: 8px;
		color: var(--portal-muted);
		font-size: 13px;
	}

	.project-heading-meta > span {
		display: inline-flex;
		align-items: center;
		gap: 5px;
	}

	.project-view-nav {
		display: flex;
		align-items: center;
		gap: 4px;
		overflow-x: auto;
	}

	.project-view-nav button {
		min-height: 43px;
		display: inline-flex;
		align-items: center;
		gap: 7px;
		padding: 0 12px;
		border: 0;
		border-bottom: 2px solid transparent;
		background: transparent;
		color: var(--portal-muted);
		font: inherit;
		font-size: 13px;
		font-weight: 650;
		white-space: nowrap;
		cursor: pointer;
	}

	.project-view-nav button:hover {
		color: var(--portal-text);
	}

	.project-view-nav button.active {
		border-bottom-color: var(--v-primary-base);
		color: var(--v-primary-base);
	}

	.project-view-nav button:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: -3px;
	}

	.project-toolbar {
		padding: 16px 16px 0;
		background: var(--portal-canvas);
	}

	.project-container {
		min-height: 0;
		flex: 1;
		padding: 16px;
	}

	.project-detail-shell {
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
		overflow: hidden;
	}

	.planning-surface {
		min-height: 560px;
		height: calc(100vh - 260px);
	}

	@media (max-width: 599px) {
		.project-workspace-header {
			padding: 14px 12px 0;
		}

		.project-heading-row {
			padding-bottom: 12px;
		}

		.project-heading-meta {
			align-items: flex-start;
			flex-direction: column;
			gap: 7px;
		}

		.project-toolbar {
			padding: 12px 12px 0;
		}

		.project-container {
			padding: 12px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
	}
</style>
